import React, { useState } from "react";
import {
  Box, Typography, Container, TextField, Button,
  Snackbar, Alert, Grid, useMediaQuery,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import RoomIcon from "@mui/icons-material/Room";
import SendIcon from "@mui/icons-material/Send";
import axios from "axios";
import config from "../../config";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const IMAGE_LARGE = "https://stagingappdata.s3.amazonaws.com/contact-us/jordan-heinrichs-UpA6QvGpLHc-unsplash.jpg";
const IMAGE_SMALL = "https://stagingappdata.s3.amazonaws.com/contact-us/shayd-johnson-3SYi9YfTXdU-unsplash.jpg";

const inputSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "rgba(255,255,255,0.05)",
    color: "#fff",
    borderRadius: "10px",
    "& fieldset": { borderColor: "rgba(255,255,255,0.12)" },
    "&:hover fieldset": { borderColor: "#FFC001" },
    "&.Mui-focused fieldset": { borderColor: "#FFC001", borderWidth: "1.5px" },
  },
  "& .MuiInputBase-input::placeholder": { color: "rgba(255,255,255,0.3)", opacity: 1 },
  "& .MuiInputBase-input": { color: "#fff" },
};

const ContactUs = () => {
  const isLarge = useMediaQuery("(min-width: 900px)");
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const fields = ["name", "subject", "email", "message"];
    const formValues = fields.reduce((acc, f) => ({ ...acc, [f]: e.target.elements.namedItem(f).value }), {});
    try {
      const res = await axios.post(`${config.SERVER_URL}/api/v1/contacts`, { contact: formValues });
      if (res.status === 201) {
        setSnackbar({ open: true, message: res.data.message, severity: "success" });
        e.target.reset();
      }
    } catch ({ response }) {
      const msg = response?.status === 422
        ? Object.keys(response.data.errors) + " " + Object.values(response.data.errors)
        : "Something went wrong. Please try again.";
      setSnackbar({ open: true, message: msg, severity: "error" });
    }
    setLoading(false);
  };

  return (
    <>
      <Helmet>
        <meta property="og:image" content="https://prodappdata.s3.amazonaws.com/preview-images/contact-us-preview.png" />
        <meta property="og:title" content="Contact Us" />
        <meta property="og:description" content="Moore Exotics Contact Us" />
      </Helmet>

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} variant="filled" severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Box sx={{ minHeight: "100vh", backgroundColor: "#111" }}>

        {/* ── Full-bleed hero image ── */}
        <Box sx={{ position: "relative", height: { xs: 280, sm: 360, md: 420 }, overflow: "hidden" }}>
          <img
            src={isLarge ? IMAGE_LARGE : IMAGE_SMALL}
            alt="Contact Moore Exotics"
            style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center 30%" }}
          />
          {/* gradient overlay */}
          <Box sx={{ position: "absolute", inset: 0, background: "linear-gradient(to bottom, rgba(0,0,0,0.35) 0%, rgba(17,17,17,0.75) 70%, #111 100%)" }} />

          {/* Hero text on the image */}
          <Container maxWidth="xl" sx={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", justifyContent: "flex-end", pb: { xs: 3, md: 5 } }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
              <Typography component={Link} to="/" sx={{ color: "rgba(255,255,255,0.6)", fontSize: "13px", textDecoration: "none", "&:hover": { color: "#FFC001" } }}>Home</Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.3)", fontSize: "13px" }}>/</Typography>
              <Typography sx={{ color: "#FFC001", fontSize: "13px", fontWeight: 600 }}>Contact</Typography>
            </Box>
            <Typography sx={{ fontWeight: 900, fontSize: { xs: "32px", md: "52px" }, color: "#fff", lineHeight: 1, textShadow: "0 2px 20px rgba(0,0,0,0.6)" }}>
              Get In <Box component="span" sx={{ color: "#FFC001" }}>Touch</Box>
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.65)", fontSize: { xs: "13px", md: "15px" }, mt: 1 }}>
              We'd love to hear from you
            </Typography>
          </Container>
        </Box>

        {/* ── Content ── */}
        <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
          <Grid container spacing={{ xs: 3, md: 5 }} alignItems="flex-start">

            {/* ── Left: info ── */}
            <Grid item xs={12} md={4}>
              <Typography sx={{ fontWeight: 800, fontSize: "18px", color: "#fff", mb: 3 }}>
                Contact Details
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
                {[
                  {
                    icon: <RoomIcon />,
                    label: "Address",
                    lines: ["Moore M&ore LLC.", "PO Box 122416", "Arlington, TX 76012"],
                  },
                  {
                    icon: <EmailIcon />,
                    label: "Email",
                    link: "mailto:admin@mooreexotics.com",
                    lines: ["admin@mooreexotics.com"],
                  },
                  {
                    icon: <LocalPhoneIcon />,
                    label: "Phone",
                    link: "tel:+18444444112",
                    lines: ["+1 (844) 444-4112"],
                  },
                ].map(({ icon, label, lines, link }, i) => (
                  <Box key={i} sx={{ display: "flex", gap: 2, py: 2.5, borderBottom: "1px solid #1e1e1e" }}>
                    <Box sx={{
                      width: 40, height: 40, borderRadius: "10px", flexShrink: 0,
                      background: "rgba(255,192,1,0.08)", border: "1px solid rgba(255,192,1,0.15)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      {React.cloneElement(icon, { sx: { color: "#FFC001", fontSize: 18 } })}
                    </Box>
                    <Box>
                      <Typography sx={{ color: "#555", fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", mb: 0.5 }}>
                        {label}
                      </Typography>
                      {lines.map((line, j) => (
                        <Typography
                          key={j}
                          component={link && j === 0 ? "a" : "span"}
                          href={link && j === 0 ? link : undefined}
                          sx={{
                            display: "block",
                            color: link && j === 0 ? "#FFC001" : "#aaa",
                            fontSize: "14px", lineHeight: 1.7,
                            textDecoration: "none",
                            ...(link ? { "&:hover": { textDecoration: "underline" } } : {}),
                          }}
                        >
                          {line}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Grid>

            {/* ── Right: form ── */}
            <Grid item xs={12} md={8}>
              <Box sx={{
                backgroundColor: "#1a1a1a",
                border: "1px solid #2a2a2a",
                borderRadius: "20px",
                p: { xs: 3, md: 4.5 },
              }}>
                <Typography sx={{ fontWeight: 800, fontSize: "22px", color: "#fff", mb: 0.5 }}>
                  Send a Message
                </Typography>
                <Typography sx={{ color: "#555", fontSize: "13px", mb: 3.5 }}>
                  Fill out the form and we'll get back to you shortly.
                </Typography>

                <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={{ color: "#666", fontSize: "12px", fontWeight: 600, mb: 0.8, textTransform: "uppercase", letterSpacing: "0.07em" }}>Name *</Typography>
                      <TextField name="name" placeholder="Your full name" size="small" fullWidth required sx={inputSx} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={{ color: "#666", fontSize: "12px", fontWeight: 600, mb: 0.8, textTransform: "uppercase", letterSpacing: "0.07em" }}>Email *</Typography>
                      <TextField name="email" type="email" placeholder="your@email.com" size="small" fullWidth required sx={inputSx} />
                    </Grid>
                  </Grid>

                  <Box>
                    <Typography sx={{ color: "#666", fontSize: "12px", fontWeight: 600, mb: 0.8, textTransform: "uppercase", letterSpacing: "0.07em" }}>Subject *</Typography>
                    <TextField name="subject" placeholder="What is this about?" size="small" fullWidth required sx={inputSx} />
                  </Box>

                  <Box>
                    <Typography sx={{ color: "#666", fontSize: "12px", fontWeight: 600, mb: 0.8, textTransform: "uppercase", letterSpacing: "0.07em" }}>Message *</Typography>
                    <TextField name="message" placeholder="Tell us how we can help…" multiline rows={6} fullWidth required sx={inputSx} />
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2, mt: 0.5 }}>
                    <Typography sx={{ color: "#444", fontSize: "12px" }}>
                      We typically respond within 1–2 business days.
                    </Typography>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={loading}
                      endIcon={<SendIcon sx={{ fontSize: "16px !important" }} />}
                      sx={{
                        height: 46, px: 4,
                        backgroundColor: "#FFC001", color: "#000",
                        fontWeight: 800, fontSize: "14px", textTransform: "none", borderRadius: "10px",
                        "&:hover": { backgroundColor: "#e6ac00" },
                        "&.Mui-disabled": { backgroundColor: "#333", color: "#666" },
                      }}
                    >
                      {loading ? "Sending…" : "Send Message"}
                    </Button>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  );
};

export default ContactUs;
